import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, CircularProgress, Fade, Grid, Modal, Paper, Typography } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { signInComplete } from '~store/account/actions'
import CircularProgressButton from '~shared/components/ProgressButton/CircularProgressButton'
import { useGlobalStyles } from '~shared/styles/globalStyles'
import { useAddToast } from '~utils/useAddToast'
import {
  getTokenSilentRedirect,
  handleRedirectPromiseAndSelectAccount,
  RedirectResponseStatus,
  signInRedirect,
  signOut,
} from '~utils/msal/msalAuthentication'
import logo from '~assets/logo-saf--big.png'
import Backdrop from '@mui/material/Backdrop'
import { useAuthenticatorStyles } from './Authenticator.styles'
import clsx from 'clsx'

export enum AuthStatus {
  notStarted = 'notStarted',
  inProgress = 'in Progress',
  failed = 'failed',
  success = 'success',
}

interface Props {
  children: React.ReactNode
}

// todo - this is not the place for this interface -- add a useModal hook
interface ModalData {
  open: boolean
  text?: string
  logoutOnClose?: boolean
}

const Authenticator: React.FC<Props> = (props) => {
  const s = useAuthenticatorStyles()
  const gs = useGlobalStyles()
  const { t } = useTranslation()
  const addToast = useAddToast()
  const dispatch = useDispatch()
  const history = useHistory()
  const [authStatus, setAuthStatus] = useState(AuthStatus.notStarted)

  const msalSignIn = useCallback(() => {
    setAuthStatus(AuthStatus.inProgress)
    signInRedirect()
      .then((redirectStatus) => {
        // run this when signInRedirect somehow fails
        if (!redirectStatus) {
          addToast({ severity: 'error', content: t('error:signInError') })
          setAuthStatus(AuthStatus.failed)
        }
      })
      .catch((e) => {
        addToast({ severity: 'error', content: t('error:signInError') })
        console.log('msalSignIn error', e)
        setAuthStatus(AuthStatus.failed)
      })
  }, [addToast, t])

  const [modalData, setModalData] = useState<ModalData>({ open: false })
  const handleOpenModal = (text: string, logoutOnClose?: boolean) => {
    setModalData({ text, logoutOnClose: logoutOnClose, open: true })
  }
  const handleCloseModal = () => {
    if (modalData.logoutOnClose) signOut()

    setModalData({ ...modalData, open: false })
  }

  // on mount
  useEffect(() => {
    setAuthStatus(AuthStatus.inProgress) // is set in "msalSignIn" function as well

    handleRedirectPromiseAndSelectAccount()
      .then((redirectResponseStatus) => {
        if (redirectResponseStatus.status === RedirectResponseStatus.profileUpdateSuccess) {
          handleOpenModal(
            t('common:profileUpdateSuccessModalText'),
            true,
          )
          return
        }

        if (redirectResponseStatus.status === RedirectResponseStatus.passwordResetSuccess) {
          handleOpenModal(
            t('common:passwordResetSuccessModalText'),
            true,
          )
          return
        }

        if (redirectResponseStatus.status === RedirectResponseStatus.resetPassword) {
          // redirect to password reset will be handled by "handleRedirectPromiseAndSelectAccount"
          return
        }

        if (
          redirectResponseStatus.accountInfo
          && redirectResponseStatus.status === RedirectResponseStatus.success
        ) {
          dispatch(signInComplete(redirectResponseStatus.accountInfo))

          // since "@azure/msal-browser": "2.14.2" - after token acquisition msal doesn't redirect to basePath anymore
          // so we have to do that by hand if it is necessary
          if (history.location.pathname.includes('login/token')) {
            history.push('')
          }

          getTokenSilentRedirect()
            .then((silentTokenAcquisitionSuccess) => {
              if (silentTokenAcquisitionSuccess) setAuthStatus(AuthStatus.success)
              else setAuthStatus(AuthStatus.failed)
            })
          // catch is not necessary, on token acquisition failed getTokenSilentRedirect will run signOut
        } else {
          msalSignIn() // setAuthStatus(AuthStatus.failed) - will be set here
        }
      })
      .catch((e) => {
        msalSignIn() // setAuthStatus(AuthStatus.failed) - will be set here
        console.log('handleRedirectPromiseAndSelectAccount unknown error', e)
      })

    // eslint-disable-next-line -- on mount
  }, [])

  return authStatus === AuthStatus.success
    ? <>{props.children}</>
    : <div className={s.authFailedContainer}>
      <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        className={s.modal}
        open={modalData.open || false}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalData.open || false}>
          <div className={s.modalPaper}>
            <Typography style={{ whiteSpace: 'pre-line' }}>{modalData.text}.</Typography>
            <div className={clsx(gs.flex, gs.flexJustifyCenter, gs.marginTopSmall)}>
              <Button
                onClick={handleCloseModal}
                variant='contained'
                color='secondary'
              >
                <Typography component='span'>
                  Ok
                </Typography>
              </Button>
            </div>
          </div>
        </Fade>
      </Modal>
      {
        authStatus === AuthStatus.failed
          ? <Paper className={s.authFailedPaper}>
            {t('error:signInError')}
            <CircularProgressButton
              text={t('common:tryAgain')}
              variant='contained'
              color='secondary'
              onClick={msalSignIn}
              className={gs.marginTopTiny}
            />
          </Paper>
          : <Grid container spacing={3}>
            <Grid container item xs={12} justifyContent='center'>
              <img
                src={logo} className={gs.logoBig}
              />
            </Grid>
            <Grid container item xs={12} justifyContent='center'>
              <CircularProgress color='primary' />
            </Grid>
          </Grid>
      }
    </div>
}

export default Authenticator
